import React, { useState } from 'react';
import { QuizResult } from '../types';
import QuestionNavigation from './QuestionNavigation';
import ReviewCard from './ReviewCard';

interface ReviewScreenProps {
    results: QuizResult;
    onExit: () => void;
    displayLanguage: 'pt' | 'en' | 'es';
}

const ReviewScreen: React.FC<ReviewScreenProps> = ({ results, onExit, displayLanguage }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const handleJumpToQuestion = (index: number) => {
        setCurrentIndex(index);
    };

    const goToPrev = () => {
        setCurrentIndex(prev => Math.max(0, prev - 1));
    };

    const goToNext = () => {
        setCurrentIndex(prev => Math.min(results.questions.length - 1, prev + 1));
    };
    
    const currentQuestion = results.questions[currentIndex];
    const userAnswer = results.answers.get(currentQuestion.id);

    return (
        <div className="w-full animate-fade-in">
             <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-lg mb-6 sticky top-4 z-10">
                <div className="flex justify-between items-center">
                    <button onClick={onExit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                        <i className="fas fa-arrow-left mr-2"></i>
                        {displayLanguage === 'en' ? 'Back to Results' : displayLanguage === 'es' ? 'Volver a resultados' : 'Voltar aos Resultados'}
                    </button>
                    <div className="font-mono text-lg font-bold">
                        {displayLanguage === 'en' ? 'Review' : displayLanguage === 'es' ? 'Revisión' : 'Revisão'}: {displayLanguage === 'en' ? 'Question' : displayLanguage === 'es' ? 'Pregunta' : 'Questão'} {currentIndex + 1}/{results.questions.length}
                    </div>
                </div>
            </div>

            <QuestionNavigation
                questions={results.questions}
                userAnswers={results.answers}
                markedForReview={new Set()} // Not relevant in review mode
                currentQuestionIndex={currentIndex}
                onJumpToQuestion={handleJumpToQuestion}
                reviewMode={true}
                displayLanguage={displayLanguage}
            />

            <ReviewCard 
                question={currentQuestion}
                userAnswer={userAnswer}
                displayLanguage={displayLanguage}
            />

            <div className="flex justify-between items-center mt-auto p-4 bg-white dark:bg-slate-800 rounded-lg shadow-lg sticky bottom-4 z-10">
                <button
                    onClick={goToPrev}
                    disabled={currentIndex === 0}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 dark:bg-slate-600 dark:hover:bg-slate-500 dark:text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500 disabled:cursor-not-allowed"
                >
                    <i className="fas fa-arrow-left mr-2"></i>
                    {displayLanguage === 'en' ? 'Previous' : displayLanguage === 'es' ? 'Anterior' : 'Anterior'}
                </button>
                <button
                    onClick={goToNext}
                    disabled={currentIndex === results.questions.length - 1}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500 disabled:cursor-not-allowed"
                >
                    {displayLanguage === 'en' ? 'Next' : displayLanguage === 'es' ? 'Siguiente' : 'Próxima'}
                    <i className="fas fa-arrow-right ml-2"></i>
                </button>
            </div>
        </div>
    );
};

export default ReviewScreen;